import { Inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { StateService } from '@uirouter/core';

import { storePurchaseAPIInjector } from 'src/app/api/infra/store-api-service.injector';
import { BillingApiService } from 'src/app/api/services/billing-api.service';
import { StorePurchaseApi } from 'src/app/api/store-api';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';
import { PlansService } from 'src/app/components/plans/plans.service';
import { ReloadSelectedCompanyService } from 'src/app/components/userstate/reload-selected-company.service';
import { AddressUtilsService } from 'src/app/shared/services/address-utils.service';
import { ContactUtilsService } from 'src/app/shared/services/contact-utils.service';

import {
  BasePurchaseDetails,
  BasePurchaseService,
} from './base-purchase.service';
import { CreditCardService } from './credit-card.service';

export const INTERACTIVE_LICENSE_MIN_QUANTITY = 1;
export const INTERACTIVE_LICENSE_DEFAULT_QUANTITY = 1;

export type InteractiveLicenseEstimate = {
  amountDueNow: number;
  currencyCode: string;
  nextRenewalDate?: number;
  nextRenewalTotal?: number;
  totalTax?: number;
  estimateError?: string;
};

@Injectable({
  providedIn: 'root',
})
export class PurchaseInteractiveService extends BasePurchaseService {
  public loading = false;
  public apiError: string | null = null;
  public purchase: BasePurchaseDetails = {};
  public estimate: InteractiveLicenseEstimate = {
    amountDueNow: 0,
    currencyCode: 'usd',
    totalTax: 0,
  };
  public couponCode = '';
  public existingInteractiveSubscriptionId: string | null = null;
  public currentInteractiveLicenseCount = 0;

  private reloadCallback: () => void;
  private purchaseIntent: { intentId?: string } | null = null;

  readonly form = new FormGroup({
    quantity: new FormControl<number>(INTERACTIVE_LICENSE_DEFAULT_QUANTITY, {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.min(INTERACTIVE_LICENSE_MIN_QUANTITY),
      ],
    }),
  });

  constructor(
    private stateService: StateService,
    private plansService: PlansService,
    private companyStateService: CompanyStateService,
    private userStateService: UserStateService,
    private currentPlanService: CurrentPlanService,
    private addressUtils: AddressUtilsService,
    private contactUtils: ContactUtilsService,
    private creditCardService: CreditCardService,
    private billingApiService: BillingApiService,
    private reloadSelectedCompany: ReloadSelectedCompanyService,
    @Inject(storePurchaseAPIInjector) private storeApiService: StorePurchaseApi,
  ) {
    super();
  }

  get selectedPlan() {
    return this.plansService.getInteractiveLicensePlan(
      this.companyStateService.isDiscountCustomer(),
    );
  }

  get totalDue(): number {
    return this.estimate.amountDueNow;
  }

  get totalInteractiveLicensesForSubscription(): number {
    const quantity = this.form.controls.quantity.value;
    if (this.existingInteractiveSubscriptionId) {
      return this.currentInteractiveLicenseCount + quantity;
    }
    return quantity;
  }

  get priceDescription(): string {
    const planName = this.currentPlanService.currentPlan?.name ?? '';
    const qty = this.form.controls.quantity.value;
    if (this.estimate.amountDueNow > 0 && qty > 0) {
      const unitPrice = this.estimate.amountDueNow / qty;
      const suffix = planName ? `. ${planName}` : '';
      return `$${unitPrice.toFixed(2)} per display license, per year${suffix}`;
    }
    return this.selectedPlan?.name ?? 'Interactive Displays';
  }

  get payButtonLabel(): string {
    if (this.totalDue > 0) {
      return `Pay $${this.totalDue.toFixed(2)} Now`;
    }
    return 'Continue';
  }

  init(): Promise<void> {
    this._clearMessages();
    this.purchase = {
      couponCode: '',
      billingAddress: this.addressUtils.copyAddress(
        this.companyStateService.getCopyOfSelectedCompany(),
      ),
      contact: this.contactUtils.copyContactObj(
        this.userStateService.getCopyOfProfile(),
      ),
      estimate: {
        taxes: [],
        subTotal: 0,
        total: 0,
        coupons: [],
        couponAmount: 0,
        totalTax: 0,
        currency: this._getCurrency(),
      },
    };
    this.form.patchValue({
      quantity: INTERACTIVE_LICENSE_DEFAULT_QUANTITY,
    });

    return this._loadExistingInteractiveSubscription()
      .then(() => this.refreshEstimate())
      .then(() =>
        this.creditCardService.initPaymentMethods(true).finally(() => {
          this.creditCardService.paymentMethods.paymentMethod = 'card';
          this.creditCardService.paymentMethods.newCreditCard.billingAddress =
            this.purchase.billingAddress;
        }),
      );
  }

  onQuantityChanged(): void {
    this.refreshEstimate();
  }

  refreshEstimate(): Promise<void> {
    if (this.form.invalid) {
      return Promise.resolve();
    }

    if (this.existingInteractiveSubscriptionId) {
      return this._estimateSubscriptionUpdate();
    }

    return this._estimateNewPurchase();
  }

  getEstimate(): Promise<void> {
    return this.refreshEstimate();
  }

  estimateAnnualSubscription(
    quantity: number,
  ): Promise<InteractiveLicenseEstimate> {
    this.form.patchValue({ quantity });

    return this.refreshEstimate().then(() => {
      if (this.estimate.estimateError) {
        throw new Error(this.estimate.estimateError);
      }

      return this.estimate;
    });
  }

  continue(): Promise<boolean> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return Promise.resolve(false);
    }

    this._syncAddOnPlan();
    this.purchase.couponCode = this.couponCode;
    return this.refreshEstimate().then(() => !this.estimate.estimateError);
  }

  cancel(): void {
    this.stateService.go('apps.billing.subscription', {
      subscriptionId: this.existingInteractiveSubscriptionId || undefined,
    });
  }

  applyCouponCode(): Promise<void> {
    this.purchase.couponCode = this.couponCode;
    return this.refreshEstimate();
  }

  clearCouponCode(): void {
    this.couponCode = '';
    this.purchase.couponCode = '';
    void this.refreshEstimate();
  }

  validatePaymentMethod(): Promise<void> {
    this.purchase.checkoutError = null;

    if (this.creditCardService.paymentMethods.paymentMethod === 'invoice') {
      return Promise.resolve();
    }

    this.loading = true;
    return this.creditCardService.validatePaymentMethod().finally(() => {
      this.loading = false;
    });
  }

  preparePaymentIntent(): Promise<void> {
    const paymentMethods = this.creditCardService.paymentMethods;

    if (paymentMethods.paymentMethod === 'invoice') {
      return Promise.resolve();
    }

    if (this.existingInteractiveSubscriptionId) {
      return this._prepareSubscriptionUpdatePayment();
    }

    this.loading = true;
    const jsonData = this._getOrderAsJson(this.form.controls.quantity.value);

    return this.storeApiService
      .preparePurchase(jsonData)
      .then((response) => {
        if (response.error) {
          this.purchase.checkoutError = response.error;
          return Promise.reject(response.error);
        }
        paymentMethods.intentResponse = response;
        if (response.authenticationRequired) {
          return this.creditCardService.handleCardAction(response.intentSecret);
        }
      })
      .catch((error) => {
        if (typeof error === 'string') {
          this.purchase.checkoutError = error;
        } else {
          this.purchase.checkoutError =
            error?.message || 'Something went wrong, please retry';
        }
        return Promise.reject(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  completePayment(): Promise<void> {
    this.purchase.checkoutError = null;
    this.loading = true;

    if (this.existingInteractiveSubscriptionId) {
      return this._completeSubscriptionUpdate();
    }

    const quantity = this.form.controls.quantity.value;
    const jsonData = this._getOrderAsJson(quantity);
    this.purchase.reloadingCompany = true;

    return this.storeApiService
      .purchase(jsonData)
      .then(() => {
        const plan = this.selectedPlan;
        if (plan?.productCode) {
          return this.reloadSelectedCompany
            .reloadPlan(
              this._getChargebeePlanId(plan.productCode),
              this.totalInteractiveLicensesForSubscription,
            )
            .catch((err) => {
              console.debug('Failed to reload company', err);
            });
        }
      })
      .then(() => {
        this.purchase.reloadingCompany = false;
      })
      .catch((result) => {
        this.purchase.checkoutError =
          result?.message || 'There was an unknown error with the payment.';
        this.purchase.reloadingCompany = false;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  onCompanyReload(reloadCallback: () => void): void {
    this.reloadCallback = reloadCallback;
    if (this.reloadCallback) {
      this.reloadCallback();
    }
  }

  private _syncAddOnPlan(): void {
    const quantity = this.form.controls.quantity.value;
    const plan = this.selectedPlan;

    this.purchase.addOnPlan = {
      name: `${this.totalInteractiveLicensesForSubscription} ${plan?.name ?? 'Interactive Licenses'}`,
      productCode: plan?.productCode,
      licenses: quantity,
      isMonthly: false,
    };
  }

  private _syncPurchaseEstimateFromInteractive(): void {
    const totalCents = Math.round(this.estimate.amountDueNow * 100);
    this.purchase.estimate = {
      ...this.purchase.estimate,
      subTotal: totalCents,
      total: totalCents,
      totalTax: this.estimate.totalTax || 0,
      taxes: [],
      coupons: [],
      couponAmount: 0,
      currency: this.estimate.currencyCode,
      estimateError: this.estimate.estimateError,
    };
  }

  private _loadExistingInteractiveSubscription(): Promise<void> {
    this.loading = true;
    return this.billingApiService
      .getSubscriptions({ count: 100 })
      .then((response) => {
        const items = response?.items || [];
        const match = items.find((item) => {
          const planId = item.subscription?.plan?.item_price_id;
          if (!planId) {
            return false;
          }
          const plan = this.plansService.getPlanById(planId);
          return this.plansService.isInteractiveLicensePlan(plan);
        });

        if (match?.subscription) {
          this.existingInteractiveSubscriptionId = match.subscription.id;
          this.currentInteractiveLicenseCount =
            match.subscription.plan?.quantity || 0;
          return;
        }

        this.existingInteractiveSubscriptionId = null;
        this.currentInteractiveLicenseCount = 0;
      })
      .catch((error) => {
        this.existingInteractiveSubscriptionId = null;
        this.currentInteractiveLicenseCount = 0;
        this.apiError =
          'We could not verify your license status. Please try again.';
        return Promise.reject(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private _estimateSubscriptionUpdate(): Promise<void> {
    const quantity = this.form.controls.quantity.value;
    const subscriptionId = this.existingInteractiveSubscriptionId;
    const plan = this.selectedPlan;
    if (!subscriptionId || !plan?.productCode) {
      return Promise.resolve();
    }

    this.loading = true;
    this._clearMessages();

    const planId = this._getChargebeePlanId(plan.productCode);
    const newQty = this.currentInteractiveLicenseCount + quantity;

    return this.billingApiService
      .estimateSubscriptionUpdate(
        [{ subscriptionId, id: planId, qty: newQty }],
        this.couponCode,
        false,
      )
      .then((result) => {
        const estimateItem = result?.items?.[0];
        this.estimate = {
          amountDueNow: this._getInvoiceTotalFromEstimate(estimateItem),
          currencyCode: this._getCurrencyFromEstimate(estimateItem),
          totalTax: this._getTotalTaxFromEstimate(estimateItem),
          nextRenewalDate: estimateItem?.invoice_estimate?.date
            ? (estimateItem.invoice_estimate.date + 365 * 24 * 3600) * 1000
            : undefined,
          nextRenewalTotal: estimateItem?.invoice_estimate?.total
            ? estimateItem.invoice_estimate.total / 100
            : undefined,
        };
        this._syncAddOnPlan();
        this._syncPurchaseEstimateFromInteractive();
      })
      .catch((error) => {
        this.estimate.estimateError =
          error?.message ||
          'An unexpected error has occurred. Please try again.';
        this.apiError = this.estimate.estimateError;
        this._syncPurchaseEstimateFromInteractive();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private _estimateNewPurchase(): Promise<void> {
    this.loading = true;
    this._clearMessages();

    return this.storeApiService
      .estimatePurchase(this._getEstimateAsJson())
      .then((results) => {
        let total = 0;
        let totalTax = 0;
        let currencyCode = this._getCurrency();

        (results?.items || []).forEach((result) => {
          const invoiceEstimate =
            result.next_invoice_estimate || result.invoice_estimate;
          if (invoiceEstimate) {
            total += invoiceEstimate.total || 0;
            currencyCode =
              invoiceEstimate.currency_code?.toLowerCase() || currencyCode;
          }
          totalTax += this._getTotalTaxFromEstimate(result);
        });

        const firstItem = results?.items?.[0];
        const invoiceEstimate =
          firstItem?.next_invoice_estimate || firstItem?.invoice_estimate;

        this.estimate = {
          amountDueNow: total / 100,
          currencyCode,
          totalTax,
          nextRenewalDate: invoiceEstimate?.date
            ? (invoiceEstimate.date + 365 * 24 * 3600) * 1000
            : undefined,
          nextRenewalTotal: invoiceEstimate?.total
            ? invoiceEstimate.total / 100
            : undefined,
        };
        this._syncAddOnPlan();
        this._syncPurchaseEstimateFromInteractive();
      })
      .catch((error) => {
        this.estimate.estimateError =
          error?.message ||
          'An unexpected error has occurred. Please try again.';
        this.apiError = this.estimate.estimateError;
        this._syncPurchaseEstimateFromInteractive();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private _prepareSubscriptionUpdatePayment(): Promise<void> {
    const subscriptionId = this.existingInteractiveSubscriptionId;
    const plan = this.selectedPlan;
    if (!subscriptionId || !plan?.productCode) {
      return Promise.resolve();
    }

    this.loading = true;
    this._clearMessages();

    const planId = this._getChargebeePlanId(plan.productCode);
    const newQty =
      this.currentInteractiveLicenseCount + this.form.controls.quantity.value;
    const companyId = this.companyStateService.getCopyOfSelectedCompany().id;

    return this.billingApiService
      .prepareUpdateSubscription(
        [{ subscriptionId, companyId, id: planId, qty: newQty }],
        this.couponCode,
        false,
      )
      .then((response) => {
        this.purchaseIntent = response;
      })
      .catch((error) => {
        if (typeof error === 'string') {
          this.purchase.checkoutError = error;
        } else {
          this.purchase.checkoutError =
            error?.message || 'Something went wrong, please retry';
        }
        return Promise.reject(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private _completeSubscriptionUpdate(): Promise<void> {
    const subscriptionId = this.existingInteractiveSubscriptionId;
    const plan = this.selectedPlan;
    if (!subscriptionId || !plan?.productCode) {
      return Promise.resolve();
    }

    const planId = this._getChargebeePlanId(plan.productCode);
    const newQty =
      this.currentInteractiveLicenseCount + this.form.controls.quantity.value;
    const companyId = this.companyStateService.getCopyOfSelectedCompany().id;
    const paymentMethods = this.creditCardService.paymentMethods;
    const card =
      paymentMethods.paymentMethod === 'invoice'
        ? null
        : {
            intentId: this.purchaseIntent?.intentId ?? null,
          };

    this.purchase.reloadingCompany = true;

    return this.billingApiService
      .updateSubscription(
        [{ subscriptionId, companyId, id: planId, qty: newQty }],
        this.couponCode,
        false,
        card,
      )
      .then(() =>
        this.reloadSelectedCompany.reloadPlan(planId, newQty).catch((err) => {
          console.debug('Failed to reload company', err);
        }),
      )
      .then(() => {
        this.purchase.reloadingCompany = false;
      })
      .catch((error) => {
        this.purchase.checkoutError =
          error?.message || 'There was an unknown error with the payment.';
        this.purchase.reloadingCompany = false;
        return Promise.reject(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private _getEstimateAsJson(): string {
    const obj = {
      billTo: this.addressUtils.copyAddress(this.purchase.billingAddress),
      shipTo: this.addressUtils.copyAddress(this.purchase.billingAddress),
      couponCode: this.couponCode,
      items: this._getOrderItems(),
    };
    return JSON.stringify(obj);
  }

  private _getOrderAsJson(quantity: number): string {
    const paymentMethods = this.creditCardService.paymentMethods;
    const items = this._getOrderItems(quantity);
    const card = paymentMethods.selectedCard;
    const intentId = paymentMethods.intentResponse
      ? paymentMethods.intentResponse.intentId
      : null;
    const cardData: any =
      paymentMethods.paymentMethod === 'invoice'
        ? null
        : {
            cardId: card?.id,
            intentId,
            setupIntentId: null,
            isDefault: card?.isDefault ? true : false,
          };

    const obj = {
      billTo: this.addressUtils.copyAddress(this.purchase.billingAddress),
      shipTo: this.addressUtils.copyAddress(this.purchase.billingAddress),
      couponCode: this.couponCode,
      items,
      purchaseOrderNumber: paymentMethods.purchaseOrderNumber,
      card: cardData,
      paymentMethodId: this.creditCardService.getPaymentMethodId(),
      invoiceImmediately: true,
      useUnbilledCharges: false,
    };
    return JSON.stringify(obj);
  }

  private _getOrderItems(quantity?: number) {
    const plan = this.selectedPlan;
    if (!plan?.productCode) {
      return [];
    }
    const qty = quantity ?? this.form.controls.quantity.value;
    return [
      {
        id: this._getChargebeePlanId(plan.productCode),
        qty,
      },
    ];
  }

  private _getChargebeePlanId(productCode: string): string {
    return `${productCode}-${this._getCurrency()}01y`;
  }

  private _getCurrency(): string {
    const country =
      this.purchase.billingAddress?.country ??
      this.companyStateService.getCopyOfSelectedCompany().country;
    return country === 'CA' ? 'cad' : 'usd';
  }

  private _getInvoiceTotalFromEstimate(estimate: any): number {
    if (!estimate) {
      return 0;
    }
    if (!estimate.subscription_estimate?.id && estimate.next_invoice_estimate) {
      return estimate.next_invoice_estimate.amount_due / 100;
    }
    return (estimate?.invoice_estimate?.amount_due || 0) / 100;
  }

  private _getCurrencyFromEstimate(estimate: any): string {
    return (
      estimate?.invoice_estimate?.currency_code?.toLowerCase() ||
      estimate?.next_invoice_estimate?.currency_code?.toLowerCase() ||
      this._getCurrency()
    );
  }

  private _getTotalTaxFromEstimate(estimate: any): number {
    if (!estimate) {
      return 0;
    }
    const lineItems =
      estimate.invoice_estimate?.line_items ||
      estimate.next_invoice_estimate?.line_items;
    if (!Array.isArray(lineItems)) {
      return 0;
    }
    return lineItems.reduce(
      (sum: number, item: any) => sum + (Number(item?.tax_amount) || 0),
      0,
    );
  }

  private _clearMessages(): void {
    this.apiError = null;
    this.estimate.estimateError = null;
  }
}
